'use client';

import { createClient } from '@/lib/supabase/client';

export interface SavedJobCard {
  id: string;
  jobNumber: string;
  date: string;
  technician: string;
  branch: string;
  vehicleReg: string;
  vin: string;
  make: string;
  model: string;
  year: string;
  mileage: string;
  faultReported: string;
  faultsFound: string;
  repairsDone: string;
  branchRepName: string;
  signatureData: string;
  parts: { partNumber: string; partName: string; qty: number; stockAvailable: number; unitPrice?: number }[];
  submittedAt: string;
  status: 'active' | 'signed' | 'closed';
}

// ── Supabase helpers ──────────────────────────────────────────────────────────

function rowToCard(row: Record<string, unknown>, parts: Record<string, unknown>[]): SavedJobCard {
  return {
    id: row.id as string,
    jobNumber: row.job_number as string,
    date: row.date as string,
    technician: row.technician as string,
    branch: row.branch as string,
    vehicleReg: row.vehicle_reg as string,
    vin: (row.vin as string) || '',
    make: (row.make as string) || '',
    model: (row.model as string) || '',
    year: (row.year as string) || '',
    mileage: (row.mileage as string) || '',
    faultReported: (row.fault_reported as string) || '',
    faultsFound: (row.faults_found as string) || '',
    repairsDone: (row.repairs_done as string) || '',
    branchRepName: (row.branch_rep_name as string) || '',
    signatureData: (row.signature_data as string) || '',
    status: (row.status as 'active' | 'signed') || 'active',
    submittedAt: (row.submitted_at as string) || new Date().toISOString(),
    parts: parts.map((p) => ({
      partNumber: p.part_number as string,
      partName: p.part_name as string,
      qty: p.qty as number,
      stockAvailable: p.stock_available as number,
      unitPrice: (p.unit_price as number) || 0,
    })),
  };
}

export async function getJobCards(): Promise<SavedJobCard[]> {
  const supabase = createClient();
  const { data: cards, error } = await supabase
    .from('job_cards')
    .select('*')
    .order('submitted_at', { ascending: false });

  if (error) {
    console.error('getJobCards error:', error.message);
    return [];
  }

  const { data: allParts } = await supabase
    .from('job_card_parts')
    .select('*');

  const partsByCard: Record<string, Record<string, unknown>[]> = {};
  (allParts || []).forEach((p) => {
    const jcId = p.job_card_id as string;
    if (!partsByCard[jcId]) partsByCard[jcId] = [];
    partsByCard[jcId].push(p as Record<string, unknown>);
  });

  return (cards || []).map((c) =>
    rowToCard(c as Record<string, unknown>, partsByCard[c.id] || [])
  );
}

export async function addJobCard(
  card: Omit<SavedJobCard, 'id' | 'submittedAt'>
): Promise<SavedJobCard> {
  const supabase = createClient();
  const id = `jc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const submittedAt = new Date().toISOString();

  const { error: cardError } = await supabase.from('job_cards').insert({
    id,
    job_number: card.jobNumber,
    date: card.date,
    technician: card.technician,
    branch: card.branch,
    vehicle_reg: card.vehicleReg,
    vin: card.vin || '',
    make: card.make || '',
    model: card.model || '',
    year: card.year || '',
    mileage: card.mileage || '',
    fault_reported: card.faultReported || '',
    faults_found: card.faultsFound || '',
    repairs_done: card.repairsDone || '',
    branch_rep_name: card.branchRepName,
    signature_data: card.signatureData,
    status: card.status,
    submitted_at: submittedAt,
  });

  if (cardError) {
    console.error('addJobCard error:', cardError.message);
    throw cardError;
  }

  if (card.parts && card.parts.length > 0) {
    const partRows = card.parts.map((p) => ({
      id: `jcp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      job_card_id: id,
      part_number: p.partNumber,
      part_name: p.partName,
      qty: p.qty,
      stock_available: p.stockAvailable,
      unit_price: p.unitPrice || 0,
    }));
    const { error: partsError } = await supabase.from('job_card_parts').insert(partRows);
    if (partsError) {
      console.error('addJobCard parts error:', partsError.message);
    }
  }

  return { ...card, id, submittedAt };
}

export async function closeJobCard(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('job_cards')
    .update({ status: 'closed' })
    .eq('id', id);
  if (error) {
    console.error('closeJobCard error:', error.message);
    throw error;
  }
}

// ── KPI helpers ──────────────────────────────────────────────────────────────

export async function getTotalJobCards(): Promise<number> {
  const supabase = createClient();
  const { count } = await supabase
    .from('job_cards')
    .select('*', { count: 'exact', head: true });
  return count || 0;
}

export async function getActiveJobCardCount(): Promise<number> {
  const supabase = createClient();
  const { count } = await supabase
    .from('job_cards')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'active');
  return count || 0;
}

export async function getAwaitingSignatureCount(): Promise<number> {
  const supabase = createClient();
  const { count } = await supabase
    .from('job_cards')
    .select('*', { count: 'exact', head: true })
    .eq('signature_data', '');
  return count || 0;
}

export async function getVehiclesServicedThisWeek(): Promise<number> {
  const supabase = createClient();
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  const monday = new Date(now);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);

  const { data } = await supabase
    .from('job_cards')
    .select('vehicle_reg')
    .gte('submitted_at', monday.toISOString());

  const unique = new Set((data || []).map((c) => (c.vehicle_reg as string).toUpperCase()));
  return unique.size;
}

export async function getTotalPartsConsumed(): Promise<number> {
  const supabase = createClient();
  const { data } = await supabase.from('job_card_parts').select('qty');
  return (data || []).reduce((sum, p) => sum + ((p.qty as number) || 0), 0);
}

// ── localStorage fallback (for offline/migration) ────────────────────────────
const STORAGE_KEY = 'fss_job_cards_v1';

export function getJobCardsLocal(): SavedJobCard[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
